import { useSettings } from '../contexts/SettingsContext';
import { formatApiAmount, makeMoney, type Money } from '../utils/currency';

/**
 * Money formatters bound to the project's currency. When the project has no
 * currency yet, `format` returns "currency not set" instead of guessing, and
 * `isSet` is false so a screen can show a prompt to choose one.
 *
 * Outside a SettingsProvider (shared inputs, isolated tests) it behaves as
 * "currency not set" rather than throwing.
 */
export function useMoney(): Money {
  let currency = '';
  try {
    const { settings } = useSettings();
    currency = settings?.currency || settings?.project?.defaultBudgetCurrency || '';
  } catch {
    currency = '';
  }
  return makeMoney(currency);
}

/**
 * Formatter for amounts the API quotes in its base currency (shipping rate
 * cards and other `*_ngn` columns): converts them to the project currency, or
 * says "currency not set" while the project has none.
 */
export function useApiAmount(): (amount: number) => string {
  const { currency } = useMoney();
  return amount => formatApiAmount(Number(amount) || 0, currency);
}
